import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { User, Site, Ticket } from '../types';

export const useSupabaseData = () => {
    const [sites, setSites] = useState<Site[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadSites = async () => {
        try {
            const { data, error } = await supabase
                .from('sites')
                .select('*')
                .order('name');

            if (error) throw error;

            const mappedSites: Site[] = (data || []).map((s: any) => ({
                id: s.id,
                code: s.code,
                name: s.name,
                address: s.address,
                region: s.region,
                contact: s.contact,
                phone: s.phone,
                email: s.email,
                password: s.password_hash, // Para compatibilidad
            }));

            setSites(mappedSites);
            return mappedSites;
        } catch (err: any) {
            console.error('Error loading sites:', err);
            setError(err.message);
            return [];
        }
    };

    const loadUsers = async () => {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('*')
                .order('name');

            if (error) throw error;

            const mappedUsers: User[] = (data || []).map((u: any) => ({
                id: u.id,
                name: u.name,
                role: u.role,
                siteId: u.site_id,
                avatar: u.avatar,
                email: u.email,
                phone: u.phone,
                password: u.password_hash, // Para compatibilidad
            }));

            setUsers(mappedUsers);
            return mappedUsers;
        } catch (err: any) {
            console.error('Error loading users:', err);
            setError(err.message);
            return [];
        }
    };

    const loadTickets = async (userId?: string, role?: string) => {
        try {
            let query = supabase
                .from('tickets')
                .select(`
          *,
          timeline_events (*)
        `)
                .order('created_at', { ascending: false });

            // Filtrar por rol (RLS también lo hace, pero por performance)
            if (role === 'STORE' && userId) {
                const { data: userData } = await supabase
                    .from('users')
                    .select('site_id')
                    .eq('id', userId)
                    .single();

                if (userData?.site_id) {
                    query = query.eq('site_id', userData.site_id);
                }
            } else if (role === 'TECH' && userId) {
                query = query.eq('assigned_to', userId);
            }

            const { data, error } = await query;

            if (error) throw error;

            const mappedTickets: Ticket[] = (data || []).map((t: any) => ({
                id: t.id,
                ticketNumber: t.ticket_number,
                siteId: t.site_id,
                siteName: t.site_name,
                type: t.type,
                category: t.category,
                subcategory: t.subcategory,
                priority: t.priority,
                status: t.status,
                description: t.description,
                createdAt: new Date(t.created_at),
                updatedAt: new Date(t.updated_at),
                assignedTo: t.assigned_to,
                assignedToName: t.assigned_to_name,
                slaDue: new Date(t.sla_due),
                evidence: [],
                impact: t.impact,
                urgency: t.urgency,
                timeline: (t.timeline_events || []).map((e: any) => ({
                    id: e.id,
                    userId: e.user_id,
                    userName: e.user_name,
                    type: e.event_type,
                    content: e.content,
                    timestamp: new Date(e.created_at),
                })),
            }));

            setTickets(mappedTickets);
            return mappedTickets;
        } catch (err: any) {
            console.error('Error loading tickets:', err);
            setError(err.message);
            return [];
        }
    };

    const loadAll = async (userId?: string, role?: string) => {
        setLoading(true);
        try {
            await Promise.all([
                loadSites(),
                loadUsers(),
                loadTickets(userId, role),
            ]);
        } finally {
            setLoading(false);
        }
    };

    return {
        sites,
        users,
        tickets,
        loading,
        error,
        loadAll,
        loadSites,
        loadUsers,
        loadTickets,
        setSites,
        setUsers,
        setTickets,
    };
};
